import { convertirImporte } from '@/components/Costos/calculo';
import type { TipoMoneda } from '@/types/costo';
import type { PresupuestoProformaDatosFormData } from '../types';

/** Importe que se editó y del que sale el otro. */
export type OrigenIgualdad = 'monto_venta' | 'base_imponible';

const MONEDA: Record<OrigenIgualdad, 'monto_venta_moneda' | 'base_imponible_moneda'> = {
  monto_venta: 'monto_venta_moneda',
  base_imponible: 'base_imponible_moneda',
};

const OPUESTO: Record<OrigenIgualdad, OrigenIgualdad> = {
  monto_venta: 'base_imponible',
  base_imponible: 'monto_venta',
};

const redondear = (valor: number) => Math.round(valor * 100) / 100;

const moneda = (valores: PresupuestoProformaDatosFormData, campo: OrigenIgualdad): TipoMoneda =>
  valores[MONEDA[campo]] ?? 'ARS';

/**
 * Con la base imponible igual al monto de venta, recalcula el importe opuesto
 * al que se editó, convertido con la cotización del dólar si están en monedas
 * distintas. Sin cotización no se puede convertir y los valores quedan igual.
 */
export const igualarMontos = (
  valores: PresupuestoProformaDatosFormData,
  origen: OrigenIgualdad
): PresupuestoProformaDatosFormData => {
  const destino = OPUESTO[origen];
  const importe = valores[origen] ?? null;

  if (importe === null) return { ...valores, [destino]: null };

  const convertido = convertirImporte(
    importe,
    moneda(valores, origen),
    moneda(valores, destino),
    valores.cotizacion_dolar
  );

  return convertido === null ? valores : { ...valores, [destino]: redondear(convertido) };
};

/**
 * Al activar la igualdad manda el monto de venta; si todavía no está cargado,
 * se calcula a partir de la base imponible.
 */
export const igualarAlActivar = (
  valores: PresupuestoProformaDatosFormData
): PresupuestoProformaDatosFormData =>
  igualarMontos(
    valores,
    (valores.monto_venta ?? null) === null && (valores.base_imponible ?? null) !== null
      ? 'base_imponible'
      : 'monto_venta'
  );

/**
 * ¿Hay que igualar importes en monedas distintas y falta la cotización para
 * convertirlos? Mientras tanto no se recalculan y el backend no deja guardar.
 */
export const faltaCotizacionParaIgualar = (valores: PresupuestoProformaDatosFormData): boolean =>
  Boolean(valores.base_imponible_igual_monto_venta) &&
  moneda(valores, 'monto_venta') !== moneda(valores, 'base_imponible') &&
  !(valores.cotizacion_dolar && valores.cotizacion_dolar > 0) &&
  ((valores.monto_venta ?? null) !== null || (valores.base_imponible ?? null) !== null);
